// @flow
import React, { Component } from 'react';
import styled, { css } from 'react-emotion';
import ImageLoader from 'react-load-image';
import ConfiguratorMain from './ConfiguratorMain';
import consts, { colors, configStructure } from '../assets/scripts/constants';
import { getNumberFromSlug } from '../assets/scripts/helpers';
import { composeQuery } from '../assets/scripts/urlComposer';
import backgroundImage from '../assets/images/background_loading.jpg';
import UrlArea from './UrlArea';
import DoorLayer from './DoorLayer';
import DoorSpinner from './DoorSpinner';

/**
 * Styles
 */

const Wrapper = styled('div')`
  display: block;
  flex-basis: 100%;

  @media (min-width: 860px) {
    display: flex;
    height: ${29 * consts.cell}em;
  }
`;

const ImageArea = styled('div')`
  position: relative;
  flex: 1 0 0%;
  padding-top: 57.5%;
  background-color: ${colors.grayLight};
  background-image: url('${backgroundImage}')};

  &,
  > div > div {
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
  }

  @media (min-width: 860px) {
    padding-top: 0;
  }

  > .imageloader {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    pointer-events: none;

    &.imageloader-loading {
      z-index: 2;
    }

    &.imageloader-failed {
      z-index: 3;
    }

    &.imageloader-loaded {
      z-index: 1;
    }
  }
`;

/**
 * Component: ConfiguratorTop
 *
 * This wraps the image area and the main configuration area.
 */
class ConfiguratorTop extends Component<
  {
    configData: { [string]: Object },
    configOptions: { [string]: Object },
    goToFinalPage: Function,
    handleConfigChange: Function,
  },
  { latestUrlCopied: string }
> {
  /**
   * Cleans up the current configuration data into usable image strings.
   *
   * @returns {[string]} Images
   */
  composeBackground() {
    /* eslint-disable camelcase */
    const {
      configData: {
        pa_kehyksen_tyyli,
        pa_yksittainen_pariovi,
        pa_oviaukon_leveys,
        pa_oviaukon_korkeus,
        pa_ovimalli,
        pa_pinta,
        pa_lasi,
        pa_viilun_suunta,
        pa_vedin,
        pa_sahkoistys,
      },
    } = this.props;

    const imageBank = process.env.REACT_APP_IMAGE_BANK_ABS_PATH || '';
    const preselects = configStructure.reduce(
      (acc, part) => ({ ...acc, [part.slug]: part.preselect }),
      {}
    );

    // Default values
    const framed = 'listallinen';
    const doubleDoor = 'pariovi';
    const doubleDoorWidth = '1200';
    const singleDoorWidth = '600';
    const defaultModel = 'ovimalli_1l';
    const glassDoor = 'lasiovi';
    const oakDoor = 'ovilehti_tammi';
    const birchDoor = 'ovilehti_koivu';
    const specialColor = 'ovilehti_erikoisvari';
    const noGlass = 'ei_lasia';
    const defaultGlassType = 'lasi_kirkas';
    const noPly = 'ei_viilua';
    const defaultPullerType = 'vedin_wc_lukitus_kiiltava_kromi';
    const defaultElectricity = 'sahkot_kylla_listallinen';

    const frameStyle =
      (pa_kehyksen_tyyli && pa_kehyksen_tyyli.value) ||
      preselects.pa_kehyksen_tyyli ||
      framed;

    let singleMulti =
      (pa_yksittainen_pariovi && pa_yksittainen_pariovi.value) ||
      preselects.pa_yksittainen_pariovi ||
      doubleDoor;
    singleMulti = singleMulti === doubleDoor ? 'double' : 'single';

    const width =
      (pa_oviaukon_leveys && getNumberFromSlug(pa_oviaukon_leveys.value)) ||
      (singleMulti === 'double'
        ? doubleDoorWidth
        : getNumberFromSlug(preselects.pa_oviaukon_leveys) || singleDoorWidth);

    let height =
      (pa_oviaukon_korkeus && getNumberFromSlug(pa_oviaukon_korkeus.value)) ||
      '2030';
    height = height === '2048' ? '2030' : height; // Rectify based on image naming.

    let model =
      (pa_ovimalli && pa_ovimalli.value) ||
      preselects.pa_ovimalli ||
      defaultModel;
    model = model.startsWith('ovimalli_') ? model.substr(9) : model;

    const surface =
      (model === glassDoor ? specialColor : false) ||
      (pa_pinta && pa_pinta.value && pa_pinta.value === specialColor
        ? 'ovilehti_valkoinen'
        : false) ||
      (pa_pinta && pa_pinta.value) ||
      preselects.pa_pinta ||
      specialColor;

    // Most doors have no glass type.
    let glass = (pa_lasi && pa_lasi.value) || noGlass;
    glass =
      glass === noGlass && (model === '1l' || model === glassDoor)
        ? defaultGlassType
        : glass;

    // We want the door closed between steps 7 and 10
    const configLen = Object.keys(this.props.configData).length;
    const doorOpen = configLen >= 7 && configLen <= 11 ? 'kiinni' : 'auki';

    // Ply direction is selected for some materials.
    let plyDir =
      (pa_viilun_suunta && pa_viilun_suunta.value) ||
      (surface === oakDoor || surface === birchDoor ? 'viilu_pysty' : false) ||
      preselects.pa_viilun_suunta ||
      noPly;
    plyDir = plyDir === noPly ? '' : `_${plyDir}`;

    // Puller materials are currently not represented visually.
    let pullerType =
      (pa_vedin && pa_vedin.value) || preselects.pa_vedin || defaultPullerType;
    pullerType =
      pullerType.indexOf('wc_lukitus') !== -1
        ? 'WC/vedin-vedin_wc_lukitus'
        : 'Normaali/vedin-vedin_pyorea';

    const electricityType =
      (pa_sahkoistys && pa_sahkoistys.value) ||
      preselects.pa_sahkoistys ||
      defaultElectricity;

    const base = `${imageBank}/Pohjakuvat/oviaukon_leveys-${width}__oviaukon_korkeus-${height}.png`;
    const door = `${imageBank}/Ovilehdet/ovimalli-${model}__oviaukon_leveys-${width}__oviaukon_korkeus-${height}__yksittainen_pariovi-${singleMulti}__pinta-${surface}${plyDir}__lasi-${glass}__oven-tila-${doorOpen}.png`;
    const frame =
      frameStyle === framed
        ? `${imageBank}/Kehykset/kehyksen_tyyli-${frameStyle}__oviaukon_leveys-${width}__oviaukon_korkeus-${height}.png`
        : '';
    const puller = `${imageBank}/Vetimet/${pullerType}__oviaukon_leveys-${width}__yksittainen_pariovi-${singleMulti}__oven-tila-${doorOpen}.png`;
    const electricity =
      electricityType.indexOf('kylla') !== -1
        ? `${imageBank}/Sahkoistys/sahkoistys-sahkoistys_kylla__oviaukon_leveys-${width}.png`
        : '';

    // $FlowFixMe
    return [base, door, frame, puller, electricity].filter(img => img !== '');
    /* eslint-enable camelcase */
  }

  render() {
    const {
      configData,
      configOptions,
      goToFinalPage,
      handleConfigChange,
    } = this.props;
    const dataParam = composeQuery({
      configOptions,
      configData,
    });
    const url = `${window.location.origin}${window.location.pathname}`;
    const urlWithData = `${url}?data=${dataParam}`;

    if (dataParam && window.history.pushState) {
      const newUrl = dataParam ? urlWithData : url;
      window.history.pushState({ path: newUrl }, '', newUrl);
    }

    return (
      <Wrapper>
        <ImageArea>
          {this.composeBackground().map(layer => (
            <ImageLoader src={layer} key={layer}>
              <DoorLayer />
              <div
                className={css`
                  z-index: -1;
                  font-size: 2.5em;
                  color: ${colors.red};
                  text-shadow: 1px 1px rgba(0, 0, 0, 0.2);

                  @media (min-width: 640px) {
                    font-size: 3em;
                  }
                `}
              >
                Kaikki kuvat eivät latautuneet!
              </div>
              <DoorSpinner />
            </ImageLoader>
          ))}
          <UrlArea urlWithData={urlWithData} />
        </ImageArea>
        <ConfiguratorMain
          configData={configData}
          configOptions={configOptions}
          goToFinalPage={goToFinalPage}
          handleConfigChange={handleConfigChange}
        />
      </Wrapper>
    );
  }
}

export default ConfiguratorTop;



// WEBPACK FOOTER //
// ./src/components/ConfiguratorTop.js